import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* Reusable horizontal scroll row with:
   - hidden scrollbar
   - left/right arrow buttons (hidden at start/end)
   - click-and-drag to scroll
   Wrap children directly (they should be flex-shrink-0 cards). */

export default function ScrollRow({ children, className = "", gap = 12, ariaLabel = "Preview row" }) {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updatePosition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updatePosition();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(updatePosition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updatePosition, children]);

  const scrollByCard = (dir) => {
    const el = ref.current;
    if (!el) return;
    const firstChild = el.firstElementChild;
    const cardW = (firstChild?.getBoundingClientRect().width || 200) + gap;
    el.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  // Drag-to-scroll
  const drag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false });

  const onMouseDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
    el.style.cursor = "grabbing";
  };
  const onMouseMove = (e) => {
    if (!drag.current.down) return;
    const el = ref.current;
    if (!el) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.scrollLeft - dx;
  };
  const endDrag = () => {
    drag.current.down = false;
    const el = ref.current;
    if (el) el.style.cursor = "grab";
  };
  const onClickCapture = (e) => {
    // Swallow click that ended a drag (so links inside don't fire)
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div className={`relative ${className}`} aria-label={ariaLabel}>
      {!atStart && (
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          data-testid="scrollrow-prev"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white text-[#0f1b2d] shadow-md border border-[#e2e8f0] hover:bg-[#f1f5f9]"
          aria-label="Previous"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      <div
        ref={ref}
        onScroll={updatePosition}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onClickCapture={onClickCapture}
        style={{ gap: `${gap}px`, cursor: "grab", userSelect: "none" }}
        className="flex overflow-x-auto scrollbar-hidden py-1"
      >
        {children}
      </div>
      {!atEnd && (
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          data-testid="scrollrow-next"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white text-[#0f1b2d] shadow-md border border-[#e2e8f0] hover:bg-[#f1f5f9]"
          aria-label="Next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
